import { create } from 'zustand';
import type { MindMapNode, Connection, MapMeta, MapSettings } from '@/model/types';
import { MindMapDocument } from '@/model/document';

export interface DocumentState {
  /** The underlying Yjs document wrapper. */
  mapDoc: MindMapDocument;
  /** Snapshot of all nodes (updated reactively from Yjs). */
  nodes: Map<string, MindMapNode>;
  /** Snapshot of all connections. */
  connections: Map<string, Connection>;
  /** Document metadata. */
  meta: MapMeta;
  /** Map settings. */
  settings: MapSettings;
  /** Revision counter (increments on every Yjs change). */
  revision: number;
}

export const useDocumentStore = create<DocumentState>(() => {
  const mapDoc = new MindMapDocument();
  return {
    mapDoc,
    nodes: new Map(),
    connections: new Map(),
    meta: mapDoc.getMeta(),
    settings: mapDoc.getSettings(),
    revision: 0,
  };
});

/** Initialize the document and set up Yjs observation. Returns cleanup fn. */
export function initializeDocument(title?: string): () => void {
  const { mapDoc } = useDocumentStore.getState();
  mapDoc.initialize(title);

  // Sync Yjs state to Zustand on every change
  const unobserve = mapDoc.observe(() => {
    useDocumentStore.setState((prev) => ({
      nodes: mapDoc.getAllNodes(),
      connections: mapDoc.getAllConnections(),
      meta: mapDoc.getMeta(),
      settings: mapDoc.getSettings(),
      revision: prev.revision + 1,
    }));
  });

  // Initial sync
  useDocumentStore.setState({
    nodes: mapDoc.getAllNodes(),
    connections: mapDoc.getAllConnections(),
    meta: mapDoc.getMeta(),
    settings: mapDoc.getSettings(),
    revision: 0,
  });

  return unobserve;
}
